import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import {
  getContentAlignment,
  getTextAlignment,
  visibilityControl,
} from '@/components/Cms/Card/Card.styles';
import { Container } from '@/components/Container';
import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';
import type { Maybe } from '@/lib/types';
import { theme } from '@/theme/getTheme';

import { getImageAndBackgroundStyles } from '../commonStyles.styles';

export const Wrapper = styled(Container)<{
  $visibleInDesktop?: boolean | null;
  $visibleInMobile?: boolean | null;
}>`
  ${({ $visibleInDesktop, $visibleInMobile }) => css`
    ${visibilityControl($visibleInDesktop, $visibleInMobile)}
  `};
`;

export const Header = styled(Box)<{
  $backgroundColor: Maybe<string>;
  $backgroundColorOpacity: Maybe<string>;
  $contentAlignment: Maybe<string>;
}>`
  display: flex;

  min-height: 309px;
  isolation: isolate;
  position: relative;

  ${({ $backgroundColor, $backgroundColorOpacity }) =>
    getImageAndBackgroundStyles(
      $backgroundColorOpacity,
      $backgroundColor,
      null,
    )}

  flex-direction: column;
  ${theme.media.md.up} {
    padding: ${({ theme }) => theme.spacing(0, 5)};
    ${({ $contentAlignment }) => getContentAlignment($contentAlignment)}
    min-height: 272px;
    flex-direction: unset;
  }
  ${theme.media.lg.up} {
    padding: 0 10%;
  }
`;

export const Heading = styled(Flex)<{ $textAlignment?: Maybe<string> }>`
  flex-direction: column;
  word-break: break-word;
  white-space: pre-wrap;
  z-index: 2;
  margin: 0 auto;

  ${({ $textAlignment, theme }) => css`
    font-size: ${theme.fontSizes.p2};
    padding: ${theme.spacing(4, 2)};
    ${getTextAlignment('center')}

    ${theme.media.md.up} {
      margin: 0;
      max-width: 660px;
      justify-content: center;
      ${getTextAlignment($textAlignment)}
    }
  `}
`;

export const Subheading = styled(Typography)<{
  $isCapitalized?: boolean | null;
  $textColor?: string | null;
}>`
  ${({ $isCapitalized }) =>
    $isCapitalized &&
    css`
      text-transform: uppercase;
    `}
  ${({ $textColor, theme }) => css`
    font-size: ${theme.fontSizes.h5};
    color: ${$textColor ?? theme.colors.default};
    margin-bottom: ${theme.spacing(1)};
    font-family: ${theme.fontFamilies.italianPlate};
  `}
`;

export const MediaWrapper = styled(Box)`
  position: relative;
  width: 100%;
  height: 154px;
  ${({ theme }) => css`
    ${theme.media.md.up} {
      top: 0;
      left: 0;
      position: absolute;
      height: 100%;
    }
  `};
  inset: 0;
  object-fit: cover;
  div {
    height: 100%;
  }
  img {
    object-fit: cover;
  }
`;

export const CTAWrappersContainer = styled.div<{
  $hasButton?: boolean;
  $textAlignment: string;
  $urlDirection?: string | null;
  $urlPosition?: string | null;
}>`
  display: flex;
  width: 100%;
  gap: 16px;
  margin-top: 16px;
  flex-direction: column;
  ${({ $textAlignment, $urlPosition }) => css`
    justify-content: ${$textAlignment};
    align-items: center;
    ${theme.media.md.up} {
      align-items: ${$urlPosition};
    }
  `}

  ${({ $urlDirection, $urlPosition, theme }) =>
    $urlDirection === 'row' &&
    css`
      ${theme.media.md.up} {
        flex-direction: row;
        justify-content: ${$urlPosition};
        align-item: unset;
      }
    `}

  ${({ $hasButton }) =>
    !$hasButton &&
    css`
      & {
        button,
        a > span {
          height: 1.25rem;
          padding-inline: 0;
          gap: 0;
        }
      }
    `}
`;
export const CTAWrapper = styled(Box)<{ $isButton?: boolean }>`
  ${({ $isButton }) =>
    $isButton &&
    css`
      width: 100%;
    `}
  ${theme.media.md.up} {
    width: unset;
  }
`;

export const FiltersWrapper = styled.div<{
  $visibleInDesktop?: boolean | null;
  $visibleInMobile?: boolean | null;
}>`
  ${({ $visibleInDesktop, $visibleInMobile }) => css`
    ${visibilityControl($visibleInDesktop, $visibleInMobile)}
  `};
`;
